import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CookieContext = createContext(null);

const DEFAULT_PREFERENCES = {
  necessary: true,
  analytics: false,
  advertising: false
};

// Helper function to update Google Consent Mode v2
function updateGoogleConsent(analyticsGranted, advertisingGranted) {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      'ad_storage': advertisingGranted ? 'granted' : 'denied',
      'ad_user_data': advertisingGranted ? 'granted' : 'denied',
      'ad_personalization': advertisingGranted ? 'granted' : 'denied',
      'analytics_storage': analyticsGranted ? 'granted' : 'denied'
    });
    console.log('Google Consent Mode updated:', { analytics: analyticsGranted, advertising: advertisingGranted });
  }
}

export const CookieProvider = ({ children }) => {
  const [preferences, setPreferences] = useState(() => {
    const saved = localStorage.getItem('cookie_consent');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return null;
      }
    }
    return null;
  });

  const [showBanner, setShowBanner] = useState(!preferences);
  const [showSettings, setShowSettings] = useState(false);

  const savePreferences = useCallback((prefs) => {
    const newPrefs = { ...DEFAULT_PREFERENCES, ...prefs, necessary: true };
    setPreferences(newPrefs);
    localStorage.setItem('cookie_consent', JSON.stringify(newPrefs));
    setShowBanner(false);
    setShowSettings(false);

    // UPDATE GOOGLE CONSENT MODE v2 - Critical for AdSense compliance
    updateGoogleConsent(newPrefs.analytics, newPrefs.advertising);

    // Load analytics/ads if consented
    if (newPrefs.analytics) {
      loadAnalytics();
    }
    if (newPrefs.advertising) {
      loadAds();
    }
  }, []);

  const acceptAll = useCallback(() => {
    savePreferences({ necessary: true, analytics: true, advertising: true });
  }, [savePreferences]);

  const rejectNonEssential = useCallback(() => {
    savePreferences({ necessary: true, analytics: false, advertising: false });
  }, [savePreferences]);

  const openSettings = useCallback(() => {
    setShowSettings(true);
  }, []);

  const closeSettings = useCallback(() => {
    setShowSettings(false);
  }, []);

  // Load scripts if already consented and update consent mode
  useEffect(() => {
    if (preferences) {
      // Update Google Consent Mode on page load if user already gave consent
      updateGoogleConsent(preferences.analytics, preferences.advertising);
      
      if (preferences.analytics) {
        loadAnalytics();
      }
      if (preferences.advertising) {
        loadAds();
      }
    }
  }, [preferences]);

  return (
    <CookieContext.Provider value={{
      preferences,
      showBanner,
      showSettings,
      savePreferences,
      acceptAll,
      rejectNonEssential,
      openSettings,
      closeSettings
    }}>
      {children}
    </CookieContext.Provider>
  );
};

export const useCookies = () => {
  const context = useContext(CookieContext);
  if (!context) {
    throw new Error('useCookies must be used within CookieProvider');
  }
  return context;
};

// Google Analytics 4 loader.
// Consent-gated: only runs when analytics consent is granted (via savePreferences or the
// mount effect below). Never runs during build-time prerender/crawl (navigator.webdriver),
// so it can't cause a network storm that breaks react-snap. Measurement ID comes from the
// environment (REACT_APP_GA_MEASUREMENT_ID), not hardcoded. GA4 is analytics, not an ad
// stack — it does not affect AdSense or the single-ad-stack policy.
function loadAnalytics() {
  if (window.gaLoaded) return;
  if (typeof navigator !== 'undefined' && navigator.webdriver) return; // skip prerender/crawl

  const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
  if (!GA_ID) {
    console.warn('GA4 disabled: set REACT_APP_GA_MEASUREMENT_ID in the build environment.');
    return;
  }
  window.gaLoaded = true;

  // The gtag() stub, dataLayer and Consent Mode v2 default are already defined in index.html.
  // Add the GA4 library + config for real users only.
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { anonymize_ip: true });
  console.log('Google Analytics 4 loaded (consent granted)');
}

// Google AdSense loader
function loadAds() {
  if (window.adsLoaded) return;
  
  const PUB_ID = 'ca-pub-3012893443790113'; // Real AdSense Publisher ID from index.html
  
  // Script already loaded in index.html, just mark as loaded
  window.adsLoaded = true;
  console.log('Google AdSense: Consent granted, ads enabled');
}

export default CookieContext;
